// Radha — CRM enhancements ported from the Nikunj CRM (mechanisms only).
//
// The forecasting math is THB's mechanism recomputed on Rasvyn's own book — no
// THB coefficients, names, POD logic or vocabulary cross over. It adjusts a
// deal's win probability for staleness: a deal untouched past a grace window is
// discounted (an idle deal is less likely to close than its stage suggests).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import type { CrmDeal } from "./useBatchModules";

const DAY = 86_400_000;
const GRACE_DAYS = 120;   // fresh within this window
const HALF_LIFE = 180;    // then win-prob halves every ~180 idle days
const FLOOR = 0.03;       // never below 3%

export function daysIdle(lastActivityAt: string | null): number {
  if (!lastActivityAt) return 9999;
  return Math.max(0, Math.floor((Date.now() - new Date(lastActivityAt).getTime()) / DAY));
}

/** Freshness discount in [FLOOR..1] from days since last activity. */
export function idleDecay(lastActivityAt: string | null): number {
  const d = daysIdle(lastActivityAt);
  if (d <= GRACE_DAYS) return 1;
  return Math.max(FLOOR, Math.pow(0.5, (d - GRACE_DAYS) / HALF_LIFE));
}

// Per-stage empirical rate from crm_stage_rates() (035). Keyed by stage.
export interface StageRate { rate: number | null; decided: number; reached: number }
export type StageRates = Map<string, StageRate>;
const MIN_DECIDED = 2; // need at least this many decided deals to trust a stage rate

export function useStageRates() {
  return useQuery({
    queryKey: ["crm-stage-rates"],
    queryFn: async (): Promise<StageRates> => {
      const { data, error } = await supabase.rpc("crm_stage_rates");
      if (error) throw error;
      const m: StageRates = new Map();
      (data as { stage: string; rate: string | null; decided: number; reached: number }[] ?? [])
        .forEach((r) => m.set(r.stage, {
          rate: r.rate == null ? null : Number(r.rate), decided: r.decided, reached: r.reached,
        }));
      return m;
    },
    staleTime: 60_000,
  });
}

/** The base win rate to use for a deal's stage: the empirical rate when a stage
 *  has enough decided history, else the deal's own modelled probability. */
export function stageBase(d: CrmDeal, rates?: StageRates): { value: number; source: "empirical" | "modelled"; n: number } {
  const e = rates?.get(d.stage);
  if (e && e.rate != null && e.decided >= MIN_DECIDED) {
    return { value: e.rate, source: "empirical", n: e.decided };
  }
  return { value: Number(d.win_probability), source: "modelled", n: e?.decided ?? 0 };
}

/** Idle-adjusted win probability. Terminal stages are certain. */
export function adjustedProb(d: CrmDeal, rates?: StageRates): number {
  if (d.stage === "won") return 1;
  if (d.stage === "lost") return 0;
  return stageBase(d, rates).value * idleDecay(d.last_activity_at);
}

export const isOpen = (d: CrmDeal) => d.stage !== "won" && d.stage !== "lost";
export const isOverdue = (d: CrmDeal) =>
  isOpen(d) && !!d.expected_close && new Date(d.expected_close).getTime() < Date.now();

/** Book win rate = won / (won + lost) over the current tenant book. */
export function bookWinRate(deals: CrmDeal[]): number | null {
  const won = deals.filter((d) => d.stage === "won").length;
  const lost = deals.filter((d) => d.stage === "lost").length;
  return won + lost === 0 ? null : won / (won + lost);
}

// ---- activity timeline (for the drawer) ------------------------------------
export interface DealActivity {
  id: string; deal_id: string; kind: string; summary: string;
  actor_name: string | null; occurred_at: string;
}

export function useDealActivities(dealId: string | null) {
  return useQuery({
    queryKey: ["crm-activities", dealId],
    enabled: !!dealId,
    queryFn: async (): Promise<DealActivity[]> => {
      const { data, error } = await supabase
        .from("deal_activities")
        .select("id, deal_id, kind, summary, actor_name, occurred_at")
        .eq("deal_id", dealId)
        .order("occurred_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as DealActivity[];
    },
  });
}

const invalidateCrm = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ["crm-direct", "crm_deals"] });
};

/** Move a deal's stage, log it to the timeline, and touch last_activity. When a
 *  deal moves to 'lost', a loss reason (and optional competitor) is recorded on
 *  the deal for Loss Analysis; moving off 'lost' clears them. */
export function useMoveStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      { deal, stage, actor, lossReason, competitor }:
      { deal: CrmDeal; stage: string; actor: string; lossReason?: string; competitor?: string },
    ) => {
      const now = new Date().toISOString();
      const patch: Record<string, unknown> = { stage, last_activity_at: now };
      if (stage === "lost") {
        patch.loss_reason = lossReason?.trim() || null;
        patch.competitor = competitor?.trim() || null;
      } else if (deal.stage === "lost") {
        patch.loss_reason = null; patch.competitor = null; // reopened — clear
      }
      const { error } = await supabase.from("crm_deals").update(patch).eq("id", deal.id);
      if (error) throw error;
      const lossNote = stage === "lost" && (lossReason?.trim() || competitor?.trim())
        ? ` (${[lossReason?.trim(), competitor?.trim() && `vs ${competitor.trim()}`].filter(Boolean).join(", ")})`
        : "";
      await supabase.from("deal_activities").insert({
        deal_id: deal.id, kind: "note",
        summary: `Stage ${deal.stage} → ${stage}${lossNote}`, actor_name: actor,
      });
    },
    onSuccess: (_r, v) => {
      invalidateCrm(qc);
      qc.invalidateQueries({ queryKey: ["crm-activities", v.deal.id] });
    },
  });
}

/** Append a free-text note to a deal's timeline and touch last_activity. */
export function useAddDealNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dealId, text, actor }: { dealId: string; text: string; actor: string }) => {
      const { error } = await supabase.from("deal_activities").insert({
        deal_id: dealId, kind: "note", summary: text, actor_name: actor,
      });
      if (error) throw error;
      await supabase.from("crm_deals").update({ last_activity_at: new Date().toISOString() }).eq("id", dealId);
    },
    onSuccess: (_r, v) => {
      invalidateCrm(qc);
      qc.invalidateQueries({ queryKey: ["crm-activities", v.dealId] });
    },
  });
}
